import React, { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { BsQuestionCircle } from "react-icons/bs";
import { getLocale } from "../../../utils/getLocale";

/**
 * Tabla principal del cotizador con todos los circuitos y métricas.
 * Componente puro: recibe valores calculados como props.
 */
export function CalculadoraTable({
  // Ascensores
  ascensoresSeleccionados,
  edificiosSeleccionadosAscensores,
  audienciaSeleccionadaAscensor,
  salidasAscensores,
  contactosCampañaAscensores,
  impactosTotalAscensores,
  tarifaFriaAscensores,
  inversionAscensoresFinal,
  // Totems
  totemsTotal,
  totemsSeleccionados,
  edificiosSeleccionadosTotems,
  audienciaSeleccionadaTotems,
  salidasTotems,
  contactosCampañaTotems,
  impactosTotalTotems,
  tarifaFriaTotems,
  inversionTotemsFinal,
  // Espera
  esperaTotal,
  esperaSeleccionados,
  edificiosSeleccionadosEspera,
  audienciaSeleccionadaEspera,
  salidasEspera,
  contactosCampañaEspera,
  impactosTotalEspera,
  tarifaFriaEspera,
  inversionEsperaFinal,
  // Cowork
  coworkTotal,
  coworkSeleccionados,
  edificiosSeleccionadosCowork,
  audienciaSeleccionadaCowork,
  salidasCowork,
  contactosCampañaCowork,
  impactosTotalCowork,
  tarifaFriaCowork,
  inversionCoworkFinal,
  // Totales
  edificiosUnicosTotal,
  audienciaSeleccionadaTotal,
  salidasTotales,
  contactosCampañaTotal,
  impactosTotales,
  tarifaFriaTotal,
  inversionFinalTotal,
  // Impactos efectivos (Poisson/IPSOS — usado como "IMPACTOS RESPALDADOS")
  impactosEfectivosPoisson,
  // Compartidos
  plazo,
  duracion,
  descuentoGlobal,
  manualDiscountPercentage,
  manualDiscountAmount,
  finalAmount,
  pais,
  selectedMoneda,
  // Modo edición: muestra el importe guardado hasta que el comercial modifique la propuesta
  opportunityData,
  hasProposalChanges,
  // Funciones
  todosLosTotalesCompletos,
  // Estilos
  styles,
}) {
  const ls = getLocale(pais);
  const fmt0 = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
  const fmt1 = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
  const fmt2 = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  // CPM: en USD los valores son chicos (US$ 1.47), se necesitan 2 decimales; PEN 1; CLP 0.
  const fmtCpm = selectedMoneda === "US$" ? fmt2 : pais === "Perú" ? fmt1 : fmt0;

  // En edición sin cambios de propuesta, respetar el importe guardado en sales.
  // Si se aplica descuento manual prevalece finalAmount; si el usuario modifica algo (hasProposalChanges)
  // pasamos al valor recomputado en runtime.
  let inversionTotalDisplay;
  if (manualDiscountAmount > 0) {
    inversionTotalDisplay = finalAmount;
  } else if (!hasProposalChanges && opportunityData?.importe) {
    inversionTotalDisplay = opportunityData.importe;
  } else {
    inversionTotalDisplay = inversionFinalTotal;
  }

  // --- Toggle CPM: 'salidas' | 'contactos' | 'impactos' | 'efectivos' ---
  const [cpmMode, setCpmMode] = useState("contactos");

  // CPM por modo: inversión / denominador × 1000
  const cpmByMode = (inversion, salidas, contactos, impactos, efectivos) => {
    if (!inversion) return 0;
    let denom = 0;
    if (cpmMode === "salidas") denom = salidas || 0;
    else if (cpmMode === "contactos") denom = contactos || 0;
    else if (cpmMode === "impactos") denom = impactos || 0;
    else if (cpmMode === "efectivos") denom = efectivos || 0;
    return denom > 0 ? (inversion / denom) * 1000 : 0;
  };

  const ipsos = impactosEfectivosPoisson || { ascensores: 0, totems: 0, espera: 0, cowork: 0, total: 0 };
  const cpmAscensoresMode = cpmByMode(
    inversionAscensoresFinal,
    salidasAscensores,
    contactosCampañaAscensores,
    impactosTotalAscensores,
    ipsos.ascensores
  );
  const cpmTotemsMode = cpmByMode(
    inversionTotemsFinal,
    salidasTotems,
    contactosCampañaTotems,
    impactosTotalTotems,
    ipsos.totems
  );
  const cpmEsperaMode = cpmByMode(
    inversionEsperaFinal,
    salidasEspera,
    contactosCampañaEspera,
    impactosTotalEspera,
    ipsos.espera
  );
  const cpmCoworkMode = cpmByMode(
    inversionCoworkFinal,
    salidasCowork,
    contactosCampañaCowork,
    impactosTotalCowork,
    ipsos.cowork
  );
  const cpmTotalMode = cpmByMode(
    inversionFinalTotal,
    salidasTotales,
    contactosCampañaTotal,
    impactosTotales,
    ipsos.total
  );

  return (
    <table
      className="table table-striped table-hover table-compact notranslate"
      style={{ tableLayout: "fixed" }}
      translate="no"
      lang="es"
    >
      <colgroup>
        <col style={{ width: "10%" }} />
        <col style={{ width: "7.36%" }} />
        <col style={{ width: "7.36%" }} />
        <col style={{ width: "7.36%" }} />
        <col style={{ width: "7.36%" }} />
        <col style={{ width: "7.36%" }} />
        <col style={{ width: "7.36%" }} />
        <col style={{ width: "7.36%" }} />
        <col style={{ width: "7.36%" }} />
        <col style={{ width: "7.36%" }} />
        <col style={{ width: "7.36%" }} />
        <col style={{ width: "7.36%" }} />
        <col style={{ width: "9%" }} />
      </colgroup>
      <thead>
        <tr>
          <th>CIRCUITO</th>
          <th>EDIFICIOS</th>
          <th>PANTALLAS</th>
          <th>
            <div className={styles.thInner}>
              <span className={styles.thTitle}>AUDIENCIA</span>
              <span className={styles.thSub}>personas únicas</span>
            </div>
          </th>
          <th>
            <div className={styles.thInner}>
              <span className={styles.thTitle}>PLAZO</span>
              <span className={styles.thSub}>días</span>
            </div>
          </th>
          <th className="bg-violet-soft">
            <div className={styles.thInner}>
              <span className={styles.thTitle}>SALIDAS</span>
              <span className={styles.thSub}>Spots</span>
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="tooltip-salidas">
                    <div style={{ textAlign: "left", lineHeight: 1.5 }}>
                      Cantidad de veces que se reproduce el anuncio. Fórmula: pantallas × días × salidas/hora × horas
                      activas.
                    </div>
                  </Tooltip>
                }
              >
                <span className={styles.helperFloating}>
                  <BsQuestionCircle size={9} />
                </span>
              </OverlayTrigger>
            </div>
          </th>
          <th className="bg-violet-light">
            <div className={styles.thInner}>
              <span className={styles.thTitle}>CONTACTOS</span>
              <span className={styles.thSub}>OTS</span>
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="tooltip-contactos">
                    <div style={{ textAlign: "left", lineHeight: 1.5 }}>
                      Oportunidades de ver el anuncio durante la campaña. Fórmula: personas únicas × 7.48 trayectos/día
                      × días.
                    </div>
                  </Tooltip>
                }
              >
                <span className={styles.helperFloating}>
                  <BsQuestionCircle size={9} />
                </span>
              </OverlayTrigger>
            </div>
          </th>
          <th className="bg-violet-medium">
            <div className={styles.thInner}>
              <span className={styles.thTitle}>IMPACTOS</span>
              <span className={styles.thSub}>VAC</span>
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="tooltip-impactos">
                    <div style={{ textAlign: "left", lineHeight: 1.5 }}>
                      <strong>VAC</strong> (Visibility Adjusted Contacts): OTS ajustado por visibilidad y frecuencia por
                      franja horaria. Audiencia con probabilidad real de haber visto el anuncio.
                    </div>
                  </Tooltip>
                }
              >
                <span className={styles.helperFloating}>
                  <BsQuestionCircle size={9} />
                </span>
              </OverlayTrigger>
            </div>
          </th>
          <th className="bg-violet">
            <div className={styles.thInner}>
              <span className={styles.thTitle}>EFECTIVOS</span>
              <span className={styles.thSub}>Poisson</span>
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="tooltip-efectivos">
                    <div style={{ textAlign: "left", lineHeight: 1.5 }}>
                      Audiencia que efectivamente vio el anuncio según modelo Poisson respaldado por investigación Ipsos
                      Chile. Atención 86%, recordación 76%.
                    </div>
                  </Tooltip>
                }
              >
                <span className={styles.helperFloating}>
                  <BsQuestionCircle size={9} />
                </span>
              </OverlayTrigger>
            </div>
          </th>
          <th className="bg-amber-dark">COTIZACIÓN</th>
          <th className="bg-amber">DESCUENTO</th>
          <th className="bg-success">INVERSIÓN</th>
          <th className="bg-primary">
            <div className={styles.cpmHeaderInner}>
              <span className={styles.cpmLabel}>
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id="tooltip-cpm">
                      <div style={{ textAlign: "left", lineHeight: 1.5 }}>
                        <strong>CPM — Costo por Mil</strong>
                        <br />
                        Cuánto cuesta llegar a 1.000 unidades de la métrica seleccionada. Es el estándar universal para
                        comparar inversión publicitaria entre medios y formatos.
                        <br />
                        <br />
                        Fórmula: <em>inversión ÷ unidad × 1.000</em>
                        <br />
                        <br />
                        <strong>· Salidas (Spots)</strong>
                        <br />
                        Costo por cada 1.000 reproducciones del spot. Refleja la <em>tarifa de pauta</em> (cuánto vale
                        cada play), no la audiencia. Lectura clásica OOH/TV: pagás por ocupar el espacio publicitario.
                        <br />
                        <br />
                        <strong>· Contactos (OTS — Opportunity to See)</strong>
                        <br />
                        Costo por cada 1.000 oportunidades de exposición. OTS = audiencia única × frecuencia × días,
                        suma todas las veces que alguien pudo ver el anuncio. Estándar industria DOOH.
                        <br />
                        <br />
                        <strong>· Impactos (VAC — Visibility Adjusted Contacts)</strong>
                        <br />
                        Costo por cada 1.000 contactos con visibilidad real. VAC ajusta el OTS por visibilidad y
                        frecuencia por franja horaria (dwell time, atención del entorno).
                        <br />
                        <br />
                        <strong>· Efectivos (modelo Poisson)</strong>
                        <br />
                        Costo por cada 1.000 personas que efectivamente registraron el anuncio. Audiencia con atención
                        (86%) y recordación (76%) según investigación Ipsos Chile. La métrica más exigente del set y
                        nuestro estándar de medición: la audiencia que sí ve, no la que podría ver. Granularidad y
                        precisión sin atajos.
                      </div>
                    </Tooltip>
                  }
                >
                  <span className={styles.cpmHelperInline}>
                    <BsQuestionCircle size={9} />
                  </span>
                </OverlayTrigger>
                CPM
              </span>
              <div className={styles.cpmModeToggle} role="tablist" aria-label="Modo de CPM">
                <button
                  type="button"
                  role="tab"
                  aria-selected={cpmMode === "salidas"}
                  className={`${styles.cpmModePill} ${cpmMode === "salidas" ? styles.cpmModePillActive : ""}`}
                  onClick={() => setCpmMode("salidas")}
                >
                  Salidas
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={cpmMode === "contactos"}
                  className={`${styles.cpmModePill} ${cpmMode === "contactos" ? styles.cpmModePillActive : ""}`}
                  onClick={() => setCpmMode("contactos")}
                >
                  Contactos
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={cpmMode === "impactos"}
                  className={`${styles.cpmModePill} ${cpmMode === "impactos" ? styles.cpmModePillActive : ""}`}
                  onClick={() => setCpmMode("impactos")}
                >
                  Impactos
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={cpmMode === "efectivos"}
                  className={`${styles.cpmModePill} ${cpmMode === "efectivos" ? styles.cpmModePillActive : ""}`}
                  onClick={() => setCpmMode("efectivos")}
                >
                  Efectivos
                </button>
              </div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {/* --- ASCENSORES --- */}
        <tr>
          <th scope="row" className="text-start">
            ASCENSORES
          </th>
          <td>{ascensoresSeleccionados ? edificiosSeleccionadosAscensores : "-"}</td>
          <td>{ascensoresSeleccionados ? ascensoresSeleccionados : "-"}</td>
          <td>{ascensoresSeleccionados ? audienciaSeleccionadaAscensor.toLocaleString(ls, fmt0) : "-"}</td>
          <td>{ascensoresSeleccionados && plazo ? plazo : "-"}</td>
          <td>{salidasAscensores ? salidasAscensores.toLocaleString(ls, fmt0) : "-"}</td>
          <td>{contactosCampañaAscensores ? Math.round(contactosCampañaAscensores).toLocaleString(ls, fmt0) : "-"}</td>
          <td>
            {impactosTotalAscensores && duracion ? Math.round(impactosTotalAscensores).toLocaleString(ls, fmt0) : "-"}
          </td>
          <td>
            {impactosEfectivosPoisson.ascensores ? impactosEfectivosPoisson.ascensores.toLocaleString(ls, fmt0) : "-"}
          </td>
          <td className="text-center">
            {tarifaFriaAscensores ? `${selectedMoneda} ${tarifaFriaAscensores.toLocaleString(ls, fmt0)}` : "-"}
          </td>
          <td className="text-center">
            {inversionAscensoresFinal
              ? descuentoGlobal && todosLosTotalesCompletos()
                ? `${(descuentoGlobal * 100).toLocaleString(ls, fmt1)}%`
                : "-"
              : "-"}
          </td>
          <td className="text-center">
            {inversionAscensoresFinal ? `${selectedMoneda} ${inversionAscensoresFinal.toLocaleString(ls, fmt0)}` : "-"}
          </td>
          <td className="text-center">
            {cpmAscensoresMode ? `${selectedMoneda} ${cpmAscensoresMode.toLocaleString(ls, fmtCpm)}` : "-"}
          </td>
        </tr>

        {/* --- CIRCULACIÓN --- */}
        {totemsTotal > 0 && (
          <tr>
            <th scope="row" className="text-start">
              CIRCULACIÓN
            </th>
            <td>{totemsSeleccionados ? edificiosSeleccionadosTotems : "-"}</td>
            <td>{totemsSeleccionados ? totemsSeleccionados : "-"}</td>
            <td>{totemsSeleccionados ? audienciaSeleccionadaTotems.toLocaleString(ls, fmt0) : "-"}</td>
            <td>{totemsSeleccionados && plazo ? plazo : "-"}</td>
            <td>{salidasTotems ? salidasTotems.toLocaleString(ls, fmt0) : "-"}</td>
            <td>{contactosCampañaTotems ? Math.round(contactosCampañaTotems).toLocaleString(ls, fmt0) : "-"}</td>
            <td>{impactosTotalTotems && duracion ? Math.round(impactosTotalTotems).toLocaleString(ls, fmt0) : "-"}</td>
            <td>{impactosEfectivosPoisson.totems ? impactosEfectivosPoisson.totems.toLocaleString(ls, fmt0) : "-"}</td>
            <td className="text-center">
              {tarifaFriaTotems ? `${selectedMoneda} ${tarifaFriaTotems.toLocaleString(ls, fmt0)}` : "-"}
            </td>
            <td className="text-center">
              {inversionTotemsFinal
                ? descuentoGlobal && todosLosTotalesCompletos()
                  ? `${(descuentoGlobal * 100).toLocaleString(ls, fmt1)}%`
                  : "-"
                : "-"}
            </td>
            <td className="text-center">
              {inversionTotemsFinal ? `${selectedMoneda} ${inversionTotemsFinal.toLocaleString(ls, fmt0)}` : "-"}
            </td>
            <td className="text-center">
              {cpmTotemsMode ? `${selectedMoneda} ${cpmTotemsMode.toLocaleString(ls, fmtCpm)}` : "-"}
            </td>
          </tr>
        )}

        {/* --- ESPERA --- */}
        {esperaTotal > 0 && (
          <tr>
            <th scope="row" className="text-start">
              ESPERA
            </th>
            <td>{esperaSeleccionados ? edificiosSeleccionadosEspera : "-"}</td>
            <td>{esperaSeleccionados ? esperaSeleccionados : "-"}</td>
            <td>{esperaSeleccionados ? audienciaSeleccionadaEspera.toLocaleString(ls, fmt0) : "-"}</td>
            <td>{esperaSeleccionados && plazo ? plazo : "-"}</td>
            <td>{salidasEspera ? salidasEspera.toLocaleString(ls, fmt0) : "-"}</td>
            <td>{contactosCampañaEspera ? Math.round(contactosCampañaEspera).toLocaleString(ls, fmt0) : "-"}</td>
            <td>{impactosTotalEspera && duracion ? Math.round(impactosTotalEspera).toLocaleString(ls, fmt0) : "-"}</td>
            <td>{impactosEfectivosPoisson.espera ? impactosEfectivosPoisson.espera.toLocaleString(ls, fmt0) : "-"}</td>
            <td className="text-center">
              {tarifaFriaEspera ? `${selectedMoneda} ${tarifaFriaEspera.toLocaleString(ls, fmt0)}` : "-"}
            </td>
            <td className="text-center">
              {inversionEsperaFinal
                ? descuentoGlobal && todosLosTotalesCompletos()
                  ? `${(descuentoGlobal * 100).toLocaleString(ls, fmt1)}%`
                  : "-"
                : "-"}
            </td>
            <td className="text-center">
              {inversionEsperaFinal ? `${selectedMoneda} ${inversionEsperaFinal.toLocaleString(ls, fmt0)}` : "-"}
            </td>
            <td className="text-center">
              {cpmEsperaMode ? `${selectedMoneda} ${cpmEsperaMode.toLocaleString(ls, fmtCpm)}` : "-"}
            </td>
          </tr>
        )}

        {/* --- COWORK --- */}
        {coworkTotal > 0 && (
          <tr>
            <th scope="row" className="text-start">
              COWORK
            </th>
            <td>{coworkSeleccionados ? edificiosSeleccionadosCowork : "-"}</td>
            <td>{coworkSeleccionados ? coworkSeleccionados : "-"}</td>
            <td>{coworkSeleccionados ? audienciaSeleccionadaCowork.toLocaleString(ls, fmt0) : "-"}</td>
            <td>{coworkSeleccionados && plazo ? plazo : "-"}</td>
            <td>{salidasCowork ? salidasCowork.toLocaleString(ls, fmt0) : "-"}</td>
            <td>{contactosCampañaCowork ? Math.round(contactosCampañaCowork).toLocaleString(ls, fmt0) : "-"}</td>
            <td>{impactosTotalCowork && duracion ? Math.round(impactosTotalCowork).toLocaleString(ls, fmt0) : "-"}</td>
            <td>{impactosEfectivosPoisson.cowork ? impactosEfectivosPoisson.cowork.toLocaleString(ls, fmt0) : "-"}</td>
            <td className="text-center">
              {tarifaFriaCowork ? `${selectedMoneda} ${tarifaFriaCowork.toLocaleString(ls, fmt0)}` : "-"}
            </td>
            <td className="text-center">
              {inversionCoworkFinal
                ? descuentoGlobal && todosLosTotalesCompletos()
                  ? `${(descuentoGlobal * 100).toLocaleString(ls, fmt1)}%`
                  : "-"
                : "-"}
            </td>
            <td className="text-center">
              {inversionCoworkFinal ? `${selectedMoneda} ${inversionCoworkFinal.toLocaleString(ls, fmt0)}` : "-"}
            </td>
            <td className="text-center">
              {cpmCoworkMode ? `${selectedMoneda} ${cpmCoworkMode.toLocaleString(ls, fmtCpm)}` : "-"}
            </td>
          </tr>
        )}

        {/* --- TOTAL --- */}
        <tr className={styles.totalRow}>
          <th scope="row" className="text-start">
            TOTAL
          </th>
          <td>{edificiosUnicosTotal ? edificiosUnicosTotal.toLocaleString(ls, fmt0) : "-"}</td>
          <td>
            {(ascensoresSeleccionados || totemsSeleccionados || coworkSeleccionados || esperaSeleccionados) > 0
              ? (ascensoresSeleccionados || 0) +
                (totemsSeleccionados || 0) +
                (coworkSeleccionados || 0) +
                (esperaSeleccionados || 0)
              : "-"}
          </td>
          <td>{audienciaSeleccionadaTotal ? audienciaSeleccionadaTotal.toLocaleString(ls, fmt0) : "-"}</td>
          <td>{edificiosUnicosTotal && plazo ? plazo : "-"}</td>
          <td>{edificiosUnicosTotal && salidasTotales ? salidasTotales.toLocaleString(ls, fmt0) : "-"}</td>
          <td>{contactosCampañaTotal ? Math.round(contactosCampañaTotal).toLocaleString(ls, fmt0) : "-"}</td>
          <td>{impactosTotales && duracion ? Math.round(impactosTotales).toLocaleString(ls, fmt0) : "-"}</td>
          <td>{impactosEfectivosPoisson.total ? impactosEfectivosPoisson.total.toLocaleString(ls, fmt0) : "-"}</td>
          <td className="text-center">
            {tarifaFriaTotal ? `${selectedMoneda} ${tarifaFriaTotal.toLocaleString(ls, fmt0)}` : "-"}
          </td>
          <td className="text-center">
            {descuentoGlobal && todosLosTotalesCompletos()
              ? manualDiscountPercentage > 0
                ? `${(descuentoGlobal * 100).toLocaleString(ls, fmt1)}% + ${manualDiscountPercentage.toLocaleString(ls, fmt0)}%`
                : `${(descuentoGlobal * 100).toLocaleString(ls, fmt1)}%`
              : "-"}
          </td>
          <td className="text-center">
            {inversionFinalTotal ? `${selectedMoneda} ${inversionTotalDisplay.toLocaleString(ls, fmt0)}` : "-"}
          </td>
          <td className="text-center">
            {cpmTotalMode && todosLosTotalesCompletos()
              ? `${selectedMoneda} ${cpmTotalMode.toLocaleString(ls, fmtCpm)}`
              : "-"}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
